export const IDENTIFICATION_COUNTRIES = ['CL', 'MX', 'CO', 'AR', 'OTHER'] as const;

export type IdentificationCountry = (typeof IDENTIFICATION_COUNTRIES)[number];

export type IdentificationType =
  | 'RUT'
  | 'RFC'
  | 'CURP'
  | 'Cédula de ciudadanía'
  | 'DNI'
  | 'CUIT';

export type IdentificationConfig = {
  type: IdentificationType;
  label: string;
  placeholder: string;
  validationMessage: string;
  inputMode: 'text' | 'numeric';
};

export const IDENTIFICATION_CONFIG: Record<IdentificationCountry, IdentificationConfig> = {
  CL: {
    type: 'RUT',
    label: 'RUT',
    placeholder: '12.345.678-9',
    validationMessage: 'Ingresa un RUT válido.',
    inputMode: 'text',
  },
  MX: {
    type: 'RFC',
    label: 'RFC o CURP',
    placeholder: 'XEXT990101NI4',
    validationMessage: 'Ingresa un RFC o CURP válido.',
    inputMode: 'text',
  },
  CO: {
    type: 'Cédula de ciudadanía',
    label: 'Cédula de ciudadanía',
    placeholder: '1234567890',
    validationMessage: 'Ingresa una cédula de ciudadanía válida.',
    inputMode: 'numeric',
  },
  AR: {
    type: 'DNI',
    label: 'DNI o CUIT',
    placeholder: '12.345.678',
    validationMessage: 'Ingresa un DNI o CUIT válido.',
    inputMode: 'numeric',
  },
  // El documento de quien no es de un país donde ADIPA opera: un DNI peruano,
  // una cédula ecuatoriana, un pasaporte. "DNI" nombra el documento de unos
  // pocos países, así que la etiqueta es genérica a propósito. El `type` sí se
  // queda en 'DNI' porque es el valor que el CRM ya recibe.
  OTHER: {
    type: 'DNI',
    label: 'Documento de identidad',
    placeholder: '12345678',
    validationMessage: 'Ingresa tu documento de identidad.',
    inputMode: 'text',
  },
};

const MEXICAN_ID_MAX_LENGTH = 18;
const RFC_PERSON_PATTERN = /^[A-Z0-9Ñ&]{4}(\d{2})(\d{2})(\d{2})[A-Z0-9]{3}$/;
const RFC_COMPANY_PATTERN = /^[A-Z0-9Ñ&]{3}(\d{2})(\d{2})(\d{2})[A-Z0-9]{3}$/;
const CURP_PATTERN = /^[A-ZÑ]{4}(\d{2})(\d{2})(\d{2})[HM][A-Z]{5}[A-Z0-9]\d$/;
const FOREIGN_DOCUMENT_PATTERN = /^[A-Z0-9]{5,20}$/;
const FOREIGN_DOCUMENT_MIN_DIGITS = 4;
const REPEATED_CHARACTER_PATTERN = /^(.)\1+$/;

const ARGENTINE_DNI_MAX_LENGTH = 8;
const ARGENTINE_ID_MAX_LENGTH = 11;
const ARGENTINE_DNI_PATTERN = /^\d{7,8}$/;
const CUIT_PREFIXES = ['20', '23', '24', '25', '26', '27', '30', '33', '34'];
const CUIT_MULTIPLIERS = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];

export function isIdentificationCountry(value: unknown): value is IdentificationCountry {
  return (
    typeof value === 'string' && IDENTIFICATION_COUNTRIES.includes(value as IdentificationCountry)
  );
}

/**
 * Lo que el legacy guarda en `billing_country` y manda por prefill: códigos
 * ISO, nombres en español ("Chile", "Mexico") y el literal "Otro". Un valor
 * que no reconocemos es un país donde ADIPA no opera, nunca el del sitio:
 * responder el del sitio es lo que hace que a un peruano se le pida un RUT.
 * Devuelve null cuando no hay nada que interpretar, para que el formulario
 * conserve su propio default.
 */
export function normalizeIdentificationCountry(value: unknown): IdentificationCountry | null {
  if (typeof value !== 'string') return null;

  const upper = value.trim().toUpperCase();
  if (upper === '') return null;

  if (isIdentificationCountry(upper)) return upper;

  return LEGACY_COUNTRY_ALIASES[upper] ?? 'OTHER';
}

const LEGACY_COUNTRY_ALIASES: Record<string, IdentificationCountry> = {
  ARGENTINA: 'AR',
  CHILE: 'CL',
  COLOMBIA: 'CO',
  MEXICO: 'MX',
  MÉXICO: 'MX',
  OTRO: 'OTHER',
};

export function getIdentificationConfig(country: unknown): IdentificationConfig {
  return IDENTIFICATION_CONFIG[isIdentificationCountry(country) ? country : 'OTHER'];
}

export function getIdentificationType(country: unknown, value?: string): IdentificationType {
  if (country === 'MX' && typeof value === 'string' && isValidCurp(normalizeMexicanId(value))) {
    return 'CURP';
  }

  if (country === 'AR' && typeof value === 'string' && isValidCuit(argentineDigits(value))) {
    return 'CUIT';
  }

  return getIdentificationConfig(country).type;
}

export function normalizeIdentificationNumber(country: unknown, value: string): string {
  const text = value.trim();

  if (!isIdentificationCountry(country)) {
    return normalizeDni(text);
  }

  if (country === 'CL') return normalizeRut(text);
  if (country === 'MX') return normalizeMexicanId(text);
  if (country === 'CO') return text.replace(/\D/g, '');
  if (country === 'AR') return text.replace(/\D/g, '');

  return normalizeDni(text);
}

export function formatIdentificationInput(country: unknown, value: string): string {
  if (!isIdentificationCountry(country)) return formatDniInput(value);

  if (country === 'CL') return formatRutInput(value);
  if (country === 'MX') return normalizeMexicanId(value).slice(0, MEXICAN_ID_MAX_LENGTH);
  if (country === 'CO') return value.replace(/\D/g, '').slice(0, 10);
  if (country === 'AR') return formatArgentineIdInput(value);

  return formatDniInput(value);
}

export function validateIdentification(country: unknown, value: string): boolean {
  if (!isIdentificationCountry(country)) return validateDni(value);

  if (country === 'CL') return validateRut(value);
  if (country === 'MX') return validateMexicanId(value);
  if (country === 'CO') return validateColombianCitizenId(value);
  if (country === 'AR') return validateArgentineId(value);

  return validateDni(value);
}

function normalizeRut(value: string) {
  const clean = value.replace(/[.\s-]/g, '').toUpperCase();
  if (clean.length < 2) return clean;

  return `${clean.slice(0, -1)}-${clean.slice(-1)}`;
}

function validateRut(value: string) {
  const normalized = normalizeRut(value);
  const match = normalized.match(/^(\d{7,8})-([\dK])$/);
  if (!match) return false;

  const [, body, checkDigit] = match;
  let multiplier = 2;
  const sum = [...body].reverse().reduce((total, digit) => {
    const nextTotal = total + Number(digit) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
    return nextTotal;
  }, 0);

  const expectedValue = 11 - (sum % 11);
  const expectedDigit =
    expectedValue === 11 ? '0' : expectedValue === 10 ? 'K' : String(expectedValue);

  return checkDigit === expectedDigit;
}

function formatRutInput(value: string) {
  const clean = value
    .replace(/[^0-9Kk]/g, '')
    .toUpperCase()
    .slice(0, 9);
  if (clean.length <= 1) return clean;

  const body = clean.slice(0, -1);
  const checkDigit = clean.slice(-1);
  const formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${formattedBody}-${checkDigit}`;
}

function normalizeMexicanId(value: string) {
  return value.replace(/[^0-9A-Za-zÑñ&]/g, '').toUpperCase();
}

function hasValidShortDate(match: RegExpMatchArray) {
  const [, year, month, day] = match;
  return isValidShortDate(Number(year), Number(month), Number(day));
}

function isValidRfc(normalized: string) {
  const match = normalized.match(RFC_PERSON_PATTERN) ?? normalized.match(RFC_COMPANY_PATTERN);
  return match ? hasValidShortDate(match) : false;
}

function isValidCurp(normalized: string) {
  const match = normalized.match(CURP_PATTERN);
  return match ? hasValidShortDate(match) : false;
}

function validateMexicanId(value: string) {
  const normalized = normalizeMexicanId(value);
  return isValidRfc(normalized) || isValidCurp(normalized);
}

function isValidShortDate(year: number, month: number, day: number) {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;

  return isValidDate(1900 + year, month, day) || isValidDate(2000 + year, month, day);
}

function isValidDate(year: number, month: number, day: number) {
  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

function validateColombianCitizenId(value: string) {
  return /^\d{6,10}$/.test(value.trim());
}

function argentineDigits(value: string) {
  return value.replace(/\D/g, '').slice(0, ARGENTINE_ID_MAX_LENGTH);
}

function formatArgentineIdInput(value: string) {
  const digits = argentineDigits(value);

  if (digits.length <= ARGENTINE_DNI_MAX_LENGTH) {
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  return [digits.slice(0, 2), digits.slice(2, 10), digits.slice(10)]
    .filter((part) => part !== '')
    .join('-');
}

function isValidCuit(digits: string) {
  if (digits.length !== ARGENTINE_ID_MAX_LENGTH) return false;
  if (!CUIT_PREFIXES.includes(digits.slice(0, 2))) return false;

  const sum = CUIT_MULTIPLIERS.reduce(
    (total, multiplier, index) => total + Number(digits[index]) * multiplier,
    0,
  );
  const remainder = 11 - (sum % 11);
  const checkDigit = remainder === 11 ? 0 : remainder;

  return checkDigit !== 10 && checkDigit === Number(digits[10]);
}

function validateArgentineId(value: string) {
  const digits = argentineDigits(value);

  return ARGENTINE_DNI_PATTERN.test(digits) || isValidCuit(digits);
}

function normalizeDni(value: string) {
  return value.replace(/\s+/g, ' ').toUpperCase();
}

function formatDniInput(value: string) {
  return normalizeDni(value)
    .replace(/[^A-Z0-9 -]/g, '')
    .slice(0, 20);
}

/**
 * Sin país no hay formato que comprobar, pero sí hay diferencia entre un
 * documento y una excusa: "No tengo" y "asdf" pasaban la regla anterior. Todo
 * documento nacional de la región trae al menos cuatro dígitos, y esa es la
 * misma regla que aplica adipa-headless al guardarlo.
 */
function validateDni(value: string) {
  const clean = cleanDni(value);

  if (FOREIGN_DOCUMENT_PATTERN.test(clean) === false) return false;
  if (REPEATED_CHARACTER_PATTERN.test(clean)) return false;

  return (clean.match(/\d/g) ?? []).length >= FOREIGN_DOCUMENT_MIN_DIGITS;
}

function cleanDni(value: string) {
  return value.replace(/[.\s-]/g, '').toUpperCase();
}
